import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, Upload, ChevronDown, ChevronRight, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { uploadImage } from "../../lib/cms";

const move = (arr, i, d) => { const n = [...arr]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; };
const Nested = {};
const tid = (k) => `cms-field-${k}`;

export const ImageField = ({ v, set, k }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { set(await uploadImage(f)); toast.success("Picture uploaded"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={tid(k)}>
      {v ? <img src={v} alt="" className="h-32 w-full rounded-lg object-cover ring-1 ring-slate-200" /> : <div className="flex h-24 items-center justify-center rounded-lg border border-dashed text-xs text-slate-500">No picture</div>}
      <div className="flex gap-2">
        <button type="button" className="btn-primary btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={`${tid(k)}-upload`}><Upload className="h-3 w-3" />{busy ? "Uploading…" : "Upload picture"}</button>
        {v && <button type="button" className="btn-ghost btn-sm" onClick={() => set("")} data-testid={`${tid(k)}-remove`}>Remove</button>}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${tid(k)}-file`} />
    </div>
  );
};

const ImagesField = ({ v = [], set, k }) => (
  <div className="space-y-2" data-testid={tid(k)}>
    <div className="grid grid-cols-3 gap-2">{v.map((src, i) => (
      <div key={`${src}-${i}`} className="group relative"><img src={src} alt="" className="aspect-square w-full rounded-md object-cover" />
        <button type="button" className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-rose-600 shadow" onClick={() => set(v.filter((_, j) => j !== i))} aria-label="Remove picture" data-testid={`${tid(k)}-remove-${i}`}><X className="h-3 w-3" /></button></div>))}</div>
    <ImageField k={`${k}-add`} v="" set={(url) => url && set([...v, url])} />
  </div>
);

const StringsField = ({ v = [], set, k }) => (
  <div className="space-y-2" data-testid={tid(k)}>
    {v.map((s, i) => <div key={i} className="flex gap-1"><input className="input" value={s} onChange={(e) => set(v.map((x, j) => (j === i ? e.target.value : x)))} />
      <button type="button" className="btn-ghost btn-sm text-rose-600" onClick={() => set(v.filter((_, j) => j !== i))} aria-label="Delete line"><Trash2 className="h-3 w-3" /></button></div>)}
    <button type="button" className="btn-outline btn-sm" onClick={() => set([...v, ""])} data-testid={`${tid(k)}-add`}><Plus className="h-3 w-3" />Add line</button>
  </div>
);

const ListItem = ({ f, it, i, n, set, del, mv }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`${tid(f.k)}-item-${i}`}>
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button type="button" className="flex flex-1 items-center gap-1 truncate text-left text-sm font-medium" onClick={() => setOpen(!open)} data-testid={`${tid(f.k)}-toggle-${i}`}>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<span className="truncate">{it[f.itemLabel] || "Untitled"}</span>{f.priceKey && <span className="ml-auto pr-2 text-xs text-slate-500">${Number(it[f.priceKey] || 0).toLocaleString()}</span>}</button>
        <button type="button" className="btn-ghost btn-sm px-1.5" disabled={i === 0} onClick={() => mv(-1)} aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
        <button type="button" className="btn-ghost btn-sm px-1.5" disabled={i === n - 1} onClick={() => mv(1)} aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
        <button type="button" className="btn-ghost btn-sm px-1.5 text-rose-600" onClick={del} aria-label="Delete" data-testid={`${tid(f.k)}-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
      </div>
      {open && <div className="space-y-3 border-t p-3"><Nested.Form fields={f.item} value={it} onChange={set} /></div>}
    </div>
  );
};

const ListField = ({ f, v = [], set }) => (
  <div className="space-y-2" data-testid={tid(f.k)}>
    {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
    {v.map((it, i) => <ListItem key={it.id || i} f={f} it={it} i={i} n={v.length} set={(x) => set(v.map((y, j) => (j === i ? x : y)))}
      del={() => window.confirm(`Delete “${it[f.itemLabel] || "this item"}”?`) && set(v.filter((_, j) => j !== i))} mv={(d) => set(move(v, i, d))} />)}
    <button type="button" className="btn-outline btn-sm" onClick={() => set([...v, { ...f.add }])} data-testid={`${tid(f.k)}-add`}><Plus className="h-3 w-3" />Add</button>
  </div>
);

const ReviewPick = ({ v = [], set }) => {
  const [revs, setRevs] = useState(null);
  useEffect(() => { api.get("/staff/traveler-reviews", { params: { status: "approved" } }).then((r) => setRevs(r.data.reviews)).catch(() => setRevs([])); }, []);
  const used = new Set(v.map((x) => x.review_id).filter(Boolean));
  const add = (r) => set([...v, { review_id: r.id, name: r.name, trip: r.trip, quote: r.quote, rating: r.rating, photo: r.photo }]);
  if (revs === null) return <p className="text-xs text-slate-500">Loading approved reviews…</p>;
  if (!revs.length) return <p className="text-xs text-slate-500" data-testid="review-pick-empty">No approved reviews yet. Approve them under Staff → Traveler Reviews, or type one in below.</p>;
  return (
    <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2" data-testid="review-pick">{revs.map((r) => (
      <div key={r.id} className="flex items-start gap-2 rounded-md bg-white p-2 text-sm" data-testid={`review-pick-${r.id}`}>
        <div className="min-w-0 flex-1"><b>{r.name}</b> <span className="text-xs text-slate-500">{"★".repeat(r.rating)} · {r.trip}</span><p className="line-clamp-2 text-xs text-slate-600">{r.quote}</p></div>
        <button type="button" className="btn-outline btn-sm" disabled={used.has(r.id)} onClick={() => add(r)} data-testid={`review-pick-add-${r.id}`}>{used.has(r.id) ? "Added" : "Add"}</button>
      </div>))}</div>
  );
};

const Input = ({ f, v, set }) => {
  const t = f.t || "text";
  if (t === "area") return <textarea className="input" rows={f.rows || 3} value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid(f.k)} />;
  if (t === "number") return <input type="number" min={f.neg ? undefined : "0"} step="any" className="input" value={v ?? ""} onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))} data-testid={tid(f.k)} />;
  if (t === "select") return <select className="input" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid(f.k)}>{f.o.map(([val, l]) => <option key={val} value={val}>{l}</option>)}</select>;
  if (t === "image") return <ImageField k={f.k} v={v} set={set} />;
  if (t === "images") return <ImagesField k={f.k} v={v} set={set} />;
  if (t === "strings") return <StringsField k={f.k} v={v} set={set} />;
  if (t === "list") return <ListField f={f} v={v} set={set} />;
  if (t === "reviewpick") return <ReviewPick v={v} set={set} />;
  if (t === "group") return <div className="space-y-3 rounded-lg bg-slate-50 p-3"><Nested.Form fields={f.fields} value={v || {}} onChange={set} /></div>;
  return <input className="input" placeholder={t === "link" ? "/quote or https://…" : undefined} value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid(f.k)} />;
};

const Field = ({ f, v, set }) => {
  if (f.t === "bool") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(v)} onChange={(e) => set(e.target.checked)} data-testid={tid(f.k)} />{f.l}</label>;
  return <div><label className="label">{f.l}</label><Input f={f} v={v} set={set} /></div>;
};

export function SchemaForm({ fields, value, onChange }) {
  return <>{fields.map((f) => <Field key={`${f.k}-${f.t || "text"}`} f={f} v={value?.[f.k]} set={(x) => onChange({ ...value, [f.k]: x })} />)}</>;
}
Nested.Form = SchemaForm;

export const SidePanel = ({ title, onClose, children, footer }) => (
  <aside className="fixed inset-y-0 right-0 z-[70] flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl sm:w-[440px]" data-testid="cms-side-panel">
    <div className="flex items-center justify-between border-b px-5 py-4"><h3 className="font-display text-2xl font-semibold">{title}</h3>
      <button className="btn-ghost btn-sm" onClick={onClose} aria-label="Close" data-testid="cms-panel-close"><X className="h-4 w-4" /></button></div>
    <div className="flex-1 space-y-4 overflow-y-auto p-5">{children}</div>
    {footer && <div className="border-t p-4">{footer}</div>}
  </aside>
);
